import {
  saveReportFilters,
  updateReportFilters,
  getReportFilters,
  deleteReportFilter,
} from '@/apis/homeAPI';
import {
  AppliedFilter,
  FilterArrayItem,
} from '@/pages/Shipments/ControlTower/types';

export type PresetAppliedFilter = AppliedFilter & {
  title?: string;
  groupTitle?: string;
};

export type FilterPresetFilters =
  | FilterArrayItem[]
  | {
      filterArray: FilterArrayItem[];
      appliedFilters: PresetAppliedFilter[];
    };

export type FilterPreset = {
  id: string;
  name: string;
  filterArray: FilterArrayItem[];
  appliedFilters: PresetAppliedFilter[];
  createdAt: number;
  updatedAt: number;
  isDefault?: boolean;
  presets?: PresetAppliedFilter[];
  filters?: FilterPresetFilters;
};

export type PresetScope = 'report' | 'shipment';

const normalizeLogic = (value: unknown): 'AND' | 'OR' | undefined => {
  const upper = String(value || '').toUpperCase();
  return upper === 'AND' || upper === 'OR' ? upper : undefined;
};

const normalizeSource = (
  value: unknown
): FilterArrayItem['source'] | undefined => {
  return value === 'manual' || value === 'saved_preset' || value === 'tab_base'
    ? value
    : undefined;
};

const serializeToBody = (p: FilterPreset, scope?: PresetScope) => ({
  ...(p.id ? { id: p.id } : {}),
  name: p.name,
  filters: {
    filterArray: (p.filterArray ?? []).map((item) => ({
      ...item,
      filterValue: Array.isArray(item.filterValue)
        ? item.filterValue.map(String)
        : [String(item.filterValue)],
    })),
    appliedFilters: (p.appliedFilters ?? []).map((filter) => ({
      ...filter,
      filterBy: filter.filterBy || 'default',
      name: filter.name ?? '',
      ...(filter.title ? { title: filter.title } : {}),
      ...(filter.groupTitle ? { groupTitle: filter.groupTitle } : {}),
    })),
  },
  ...(scope ? { filter_type: scope } : {}),
  ...(typeof p.isDefault === 'boolean' ? { is_default: p.isDefault } : {}),
});

const deserializeAppliedFilter = (
  item: Record<string, unknown>
): PresetAppliedFilter => {
  const logic = normalizeLogic(item.logic);
  const source = normalizeSource(item.source);

  return {
    ...item,
    id: String(item.id || ''),
    name: String(item.name || ''),
    ...(item.title && { title: String(item.title) }),
    ...(item.filterBy && { filterBy: String(item.filterBy) }),
    ...(item.groupTitle && { groupTitle: String(item.groupTitle) }),
    ...(item.dateFilter && { dateFilter: String(item.dateFilter) }),
    ...(logic && { logic }),
    ...(source && { source }),
  };
};

const deserializeFromServer = (serverItem: unknown): FilterPreset => {
  const safeItem =
    serverItem && typeof serverItem === 'object'
      ? (serverItem as Record<string, unknown>)
      : {};

  const rawFilters = safeItem.filters;
  const rawPresets = Array.isArray(safeItem.presets) ? safeItem.presets : [];

  const isFiltersArray = Array.isArray(rawFilters);
  const filtersObject =
    rawFilters && typeof rawFilters === 'object' && !Array.isArray(rawFilters)
      ? (rawFilters as Record<string, unknown>)
      : null;

  const rawFilterArray: unknown[] = (() => {
    if (isFiltersArray) {
      return rawFilters as unknown[];
    }

    if (filtersObject && Array.isArray(filtersObject.filterArray)) {
      return filtersObject.filterArray as unknown[];
    }

    return [];
  })();

  const filterArray: FilterArrayItem[] = rawFilterArray
    .filter(
      (item): item is Record<string, unknown> =>
        !!item &&
        typeof item === 'object' &&
        'filterBy' in item &&
        'filterValue' in item
    )
    .map((item) => ({
      filterBy: String(item.filterBy || ''),
      filterValue: Array.isArray(item.filterValue)
        ? item.filterValue.map(String)
        : [String(item.filterValue || '')],
      ...(normalizeLogic(item.logic) && { logic: normalizeLogic(item.logic) }),
      ...(normalizeSource(item.source) && {
        source: normalizeSource(item.source),
      }),
    }));

  const rawAppliedFilters: unknown[] = (() => {
    if (filtersObject && Array.isArray(filtersObject.appliedFilters)) {
      return filtersObject.appliedFilters as unknown[];
    }

    return [];
  })();

  const appliedFilters: PresetAppliedFilter[] = rawAppliedFilters
    .filter(
      (item): item is Record<string, unknown> =>
        !!item && typeof item === 'object' && 'id' in item && 'name' in item
    )
    .map(deserializeAppliedFilter);

  const presets: PresetAppliedFilter[] = rawPresets
    .filter(
      (item): item is Record<string, unknown> =>
        !!item && typeof item === 'object' && 'id' in item && 'name' in item
    )
    .map(deserializeAppliedFilter);

  const createdAt =
    safeItem.created_at && typeof safeItem.created_at === 'string'
      ? Date.parse(safeItem.created_at)
      : Date.now();

  const updatedAt =
    safeItem.updated_at && typeof safeItem.updated_at === 'string'
      ? Date.parse(safeItem.updated_at)
      : createdAt;

  return {
    id: String(safeItem.id || ''),
    name: String(safeItem.name || ''),
    filterArray,
    appliedFilters,
    createdAt: Number.isFinite(createdAt) ? createdAt : Date.now(),
    updatedAt: Number.isFinite(updatedAt) ? updatedAt : Date.now(),
    isDefault: !!(
      safeItem.is_default || (safeItem as { is_default?: unknown }).is_default
    ),
    presets,
    filters: isFiltersArray
      ? filterArray
      : {
          filterArray,
          appliedFilters,
        },
  };
};

export const fetchPresets = async (
  token: string,
  scope: PresetScope = 'report'
): Promise<FilterPreset[]> => {
  const res = await getReportFilters(token, scope);
  const list = res?.data?.data?.data ?? [];
  return (Array.isArray(list) ? list : []).map(deserializeFromServer);
};

export const savePresetViaApi = async (
  token: string,
  preset: FilterPreset,
  opts?: { overwriteId?: string | null; scope?: PresetScope }
): Promise<FilterPreset> => {
  const processedPreset: FilterPreset = {
    ...preset,
    id: opts?.overwriteId ?? preset.id ?? '',
    filterArray: preset.filterArray.map((item) => ({
      ...item,
      filterValue: Array.isArray(item.filterValue)
        ? item.filterValue.map(String)
        : [String(item.filterValue)],
    })),
  };

  const scope = opts?.scope;
  const body = serializeToBody(processedPreset, scope);
  const isOverwrite = !!processedPreset.id;

  const res = isOverwrite
    ? await updateReportFilters(token, processedPreset.id, body)
    : await saveReportFilters(token, body);

  const item = res?.data?.data?.data ?? res?.data?.data ?? res?.data ?? body;
  return deserializeFromServer(item);
};

export const deletePresetViaApi = async (
  token: string,
  id: string
): Promise<void> => {
  await deleteReportFilter(token, id);
};
